/**
 * @fileoverview Interactive A4 margins diagram with Word-style rulers.
 *
 * Renders the page at a fixed scale with a horizontal ruler along the top
 * and a vertical ruler down the left, ticked in the user's margin unit.
 * Each zone (header / body / footer) is drawn as its own dashed content box
 * whose edges are drag handles: left/right edges set that zone's Left /
 * Right margin, the header's top edge sets Header Top, the body's top edge
 * sets Body Top, and the footer's bottom edge sets Footer Bottom.
 *
 * The component is controlled: `values` are the committed px values,
 * `onChange` fires continuously while dragging (integer px), `onCommit`
 * fires once on release so the host can persist.
 */

import { useCallback, useRef, useState } from "react";
import { cn } from "@/utils/misc";
import {
  MARGIN_LIMITS,
  formatForUnit,
  pxToCm,
  type MarginUnit,
} from "@/utils/branding/marginUnits";
import type { ZoneLocks } from "@/utils/branding/pageFit";

const A4_W = 794;
const A4_H = 1123;
const PX_PER_CM = 96 / 2.54;
const RULER_PX = 18;
/** Zone heights in the diagram (px @ page scale) — headers/footers don't have a real height here. */
const HEADER_ZONE_H = 160;
const FOOTER_ZONE_H = 120;
const MIN_COLUMN_PX = 96;

export type DiagramMarginKey =
  | "headerTopPadding"
  | "headerLeftPadding"
  | "headerRightPadding"
  | "bodyTopPadding"
  | "bodyLeftPadding"
  | "bodyRightPadding"
  | "bodyBottomPadding"
  | "footerBottomPadding"
  | "footerLeftPadding"
  | "footerRightPadding";

export type DiagramValues = Record<DiagramMarginKey, number>;

export interface MarginsRulerDiagramProps {
  values: DiagramValues;
  unit: MarginUnit;
  /** Rendered page width in CSS px (A4 aspect is preserved). */
  width?: number;
  disabled?: boolean;
  /**
   * Zones whose artwork is page-fit: their margins don't apply, so they get
   * no handles and draw edge to edge.
   */
  lockedZones?: Partial<ZoneLocks>;
  onChange: (key: DiagramMarginKey, px: number) => void;
  onCommit: (key: DiagramMarginKey, px: number) => void;
}

type Axis = "x" | "y";

interface Handle {
  key: DiagramMarginKey;
  axis: Axis;
  /** For x-axis handles: which page edge the value is measured from. */
  from: "left" | "right" | "top" | "bottom";
  /** Zone box (page px) the handle sits on. */
  zone: "header" | "body" | "footer";
}

const HANDLES: Handle[] = [
  { key: "headerTopPadding", axis: "y", from: "top", zone: "header" },
  { key: "headerLeftPadding", axis: "x", from: "left", zone: "header" },
  { key: "headerRightPadding", axis: "x", from: "right", zone: "header" },
  { key: "bodyTopPadding", axis: "y", from: "top", zone: "body" },
  { key: "bodyLeftPadding", axis: "x", from: "left", zone: "body" },
  { key: "bodyRightPadding", axis: "x", from: "right", zone: "body" },
  { key: "bodyBottomPadding", axis: "y", from: "bottom", zone: "body" },
  { key: "footerBottomPadding", axis: "y", from: "bottom", zone: "footer" },
  { key: "footerLeftPadding", axis: "x", from: "left", zone: "footer" },
  { key: "footerRightPadding", axis: "x", from: "right", zone: "footer" },
];

const HANDLE_LABEL: Record<DiagramMarginKey, string> = {
  headerTopPadding: "Header top",
  headerLeftPadding: "Header left",
  headerRightPadding: "Header right",
  bodyTopPadding: "Body top",
  bodyLeftPadding: "Body left",
  bodyRightPadding: "Body right",
  bodyBottomPadding: "Body bottom",
  footerBottomPadding: "Footer bottom",
  footerLeftPadding: "Footer left",
  footerRightPadding: "Footer right",
};

function ticks(lengthPx: number, unit: MarginUnit) {
  const out: Array<{
    at: number;
    kind: "minor" | "medium" | "major";
    label?: string;
  }> = [];
  if (unit === "cm") {
    const total = pxToCm(lengthPx);
    for (let i = 0; i * 0.25 <= total; i++) {
      const cm = i * 0.25;
      const at = cm * PX_PER_CM;
      if (i % 4 === 0)
        out.push({ at, kind: "major", label: cm === 0 ? "" : String(cm) });
      else if (i % 2 === 0) out.push({ at, kind: "medium" });
      else out.push({ at, kind: "minor" });
    }
  } else {
    for (let at = 0; at <= lengthPx; at += 10) {
      if (at % 100 === 0)
        out.push({ at, kind: "major", label: at === 0 ? "" : String(at) });
      else if (at % 50 === 0) out.push({ at, kind: "medium" });
      else out.push({ at, kind: "minor" });
    }
  }
  return out;
}

export function MarginsRulerDiagram({
  values,
  unit,
  width = 397,
  disabled,
  lockedZones,
  onChange,
  onCommit,
}: MarginsRulerDiagramProps) {
  const isLocked = (zone: Handle["zone"]) =>
    zone === "header"
      ? !!lockedZones?.header
      : zone === "footer"
        ? !!lockedZones?.footer
        : false;
  const scale = width / A4_W;
  const height = Math.round(A4_H * scale);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState<DiagramMarginKey | null>(null);
  const liveRef = useRef<{ key: DiagramMarginKey; px: number } | null>(null);

  // Zone geometry in page px.
  const bodyTop = HEADER_ZONE_H;
  const bodyBottom = A4_H - FOOTER_ZONE_H;
  const zoneBox = (zone: Handle["zone"]) =>
    zone === "header"
      ? { top: 0, bottom: HEADER_ZONE_H }
      : zone === "body"
        ? { top: bodyTop, bottom: bodyBottom }
        : { top: bodyBottom, bottom: A4_H };

  const clamp = useCallback(
    (h: Handle, raw: number) => {
      const max: number = MARGIN_LIMITS.px.max;
      const limit: number =
        h.axis === "x"
          ? (() => {
              const otherKey = (
                h.from === "left"
                  ? h.key.replace("Left", "Right")
                  : h.key.replace("Right", "Left")
              ) as DiagramMarginKey;
              return Math.min(max, A4_W - values[otherKey] - MIN_COLUMN_PX);
            })()
          : max;
      return Math.max(0, Math.min(limit, Math.round(raw)));
    },
    [values],
  );

  const begin = (h: Handle) => (e: React.PointerEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setActive(h.key);
    liveRef.current = { key: h.key, px: values[h.key] };
  };

  const move = (h: Handle) => (e: React.PointerEvent) => {
    if (active !== h.key || !pageRef.current) return;
    const rect = pageRef.current.getBoundingClientRect();
    const s = rect.width / A4_W;
    const raw: number =
      h.axis === "x"
        ? (() => {
            const x = (e.clientX - rect.left) / s;
            return h.from === "left" ? x : A4_W - x;
          })()
        : (() => {
            const y = (e.clientY - rect.top) / s;
            const box = zoneBox(h.zone);
            return h.from === "top" ? y - box.top : box.bottom - y;
          })();
    const px = clamp(h, raw);
    if (liveRef.current?.px === px) return;
    liveRef.current = { key: h.key, px };
    onChange(h.key, px);
  };

  const end = (h: Handle) => (e: React.PointerEvent) => {
    if (active !== h.key) return;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      // already released
    }
    const px = liveRef.current?.px ?? values[h.key];
    setActive(null);
    liveRef.current = null;
    onCommit(h.key, px);
  };

  const sx = (px: number) => px * scale;
  const hTicks = ticks(A4_W, unit);
  const vTicks = ticks(A4_H, unit);

  const readout = active
    ? `${HANDLE_LABEL[active]}: ${formatForUnit(liveRef.current?.px ?? values[active], unit)} ${unit}`
    : null;

  return (
    <div
      className="flex select-none flex-col items-center"
      data-testid="margins-ruler-diagram"
    >
      <div
        className="grid"
        style={{
          gridTemplateColumns: `${RULER_PX}px ${width}px`,
          gridTemplateRows: `${RULER_PX}px ${height}px`,
        }}
      >
        <div />
        {/* Horizontal ruler */}
        <div className="relative border-b border-border/60 bg-secondary/40">
          {hTicks.map((t, i) => (
            <div
              key={i}
              className={cn(
                "absolute bottom-0 w-px bg-primary/40",
                t.kind === "major"
                  ? "h-2.5"
                  : t.kind === "medium"
                    ? "h-1.5"
                    : "h-1",
              )}
              style={{ left: sx(t.at) }}
            >
              {t.label && (
                <span className="absolute -top-0.5 left-0 -translate-x-1/2 -translate-y-1/2 text-[8px] tabular-nums text-primary/55">
                  {t.label}
                </span>
              )}
            </div>
          ))}
        </div>
        {/* Vertical ruler */}
        <div className="relative border-r border-border/60 bg-secondary/40">
          {vTicks.map((t, i) => (
            <div
              key={i}
              className={cn(
                "absolute right-0 h-px bg-primary/40",
                t.kind === "major"
                  ? "w-2.5"
                  : t.kind === "medium"
                    ? "w-1.5"
                    : "w-1",
              )}
              style={{ top: sx(t.at) }}
            >
              {t.label && (
                <span className="absolute -left-0.5 top-0 -translate-x-full -translate-y-1/2 text-[8px] tabular-nums text-primary/55">
                  {t.label}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Page */}
        <div
          ref={pageRef}
          className="relative overflow-hidden border border-border/60 bg-white text-[9px] shadow-sm"
          style={{ width, height }}
        >
          {(["header", "body", "footer"] as const).map((zone) => {
            const box = zoneBox(zone);
            const locked = isLocked(zone);
            const left = locked
              ? 0
              : values[`${zone}LeftPadding` as DiagramMarginKey];
            const right = locked
              ? 0
              : values[`${zone}RightPadding` as DiagramMarginKey];
            const top =
              zone === "footer" || locked
                ? 0
                : values[`${zone}TopPadding` as DiagramMarginKey];
            const bottom = locked
              ? 0
              : zone === "footer"
                ? values.footerBottomPadding
                : zone === "body"
                  ? values.bodyBottomPadding
                  : 0;
            return (
              <div
                key={zone}
                className={cn(
                  "absolute left-0 right-0",
                  zone === "body" ? "bg-primary/[0.025]" : "bg-primary/[0.05]",
                )}
                style={{ top: sx(box.top), height: sx(box.bottom - box.top) }}
              >
                <div
                  className={cn(
                    "absolute flex items-center justify-center rounded-sm border bg-white text-primary/45",
                    zone === "body" ? "border-primary/20" : "border-primary/25",
                  )}
                  style={{
                    left: sx(left),
                    right: sx(right),
                    top: sx(top),
                    bottom: sx(bottom),
                    // Dashed = draggable margin. The header's bottom edge and
                    // the footer's top edge are where the next zone begins
                    // (not margins), so they draw as faint solid lines; the
                    // body's bottom edge IS a margin (Body › Bottom).
                    borderStyle: locked ? "solid" : "dashed",
                    ...(zone === "header"
                      ? {
                          borderBottomStyle: "solid",
                          borderBottomColor: "rgba(0,0,0,0.08)",
                        }
                      : zone === "footer"
                        ? {
                            borderTopStyle: "solid",
                            borderTopColor: "rgba(0,0,0,0.08)",
                          }
                        : {}),
                  }}
                >
                  <span className="capitalize">
                    {locked ? `${zone} · set by letterhead` : zone}
                  </span>
                </div>
              </div>
            );
          })}

          {/* Drag handles */}
          {HANDLES.filter((h) => !isLocked(h.zone)).map((h) => {
            const box = zoneBox(h.zone);
            const v = values[h.key];
            const isActive = active === h.key;
            const style: React.CSSProperties =
              h.axis === "x"
                ? {
                    top: sx(box.top),
                    height: sx(box.bottom - box.top),
                    left: h.from === "left" ? sx(v) : undefined,
                    right: h.from === "right" ? sx(v) : undefined,
                    width: 8,
                    transform: "translateX(-50%)",
                    ...(h.from === "right"
                      ? { transform: "translateX(50%)" }
                      : {}),
                  }
                : {
                    left: sx(
                      values[`${h.zone}LeftPadding` as DiagramMarginKey],
                    ),
                    right: sx(
                      values[`${h.zone}RightPadding` as DiagramMarginKey],
                    ),
                    top: h.from === "top" ? sx(box.top + v) : undefined,
                    bottom:
                      h.from === "bottom"
                        ? sx(A4_H - box.bottom + v)
                        : undefined,
                    height: 8,
                    transform:
                      h.from === "top" ? "translateY(-50%)" : "translateY(50%)",
                  };
            return (
              <button
                key={h.key}
                type="button"
                aria-label={`Drag to set ${HANDLE_LABEL[h.key].toLowerCase()} margin`}
                title={HANDLE_LABEL[h.key]}
                data-testid={`margin-handle-${h.key}`}
                disabled={disabled}
                onPointerDown={begin(h)}
                onPointerMove={move(h)}
                onPointerUp={end(h)}
                onPointerCancel={end(h)}
                className={cn(
                  "absolute z-10 touch-none rounded-sm bg-transparent transition-colors",
                  disabled
                    ? "cursor-default"
                    : h.axis === "x"
                      ? "cursor-ew-resize hover:bg-primary/15"
                      : "cursor-ns-resize hover:bg-primary/15",
                  isActive && "bg-primary/25",
                )}
                style={style}
              />
            );
          })}

          {/* Guide line + readout while dragging */}
          {active && readout && (
            <div className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 rounded bg-primary px-1.5 py-0.5 text-[10px] font-medium text-primary-foreground shadow">
              {readout}
            </div>
          )}
        </div>
      </div>
      {!disabled && (
        <p className="mt-2 max-w-md text-center text-[10px] text-primary/35">
          Drag any dashed edge to adjust that margin; values save when you let
          go. The body's bottom edge sets the gap before the footer; the
          footer's own height comes from its letterhead.
        </p>
      )}
    </div>
  );
}
